import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import IkiSymbol from '../components/IkiSymbol';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function PsychedeliaAdmin() {
  // Check for edit parameter in URL
  const urlParams = new URLSearchParams(window.location.search);
  const editPostId = urlParams.get('edit');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [status, setStatus] = useState('published');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (editPostId) {
      loadPostForEditing(editPostId);
    }
  }, [editPostId]);

  const loadPostForEditing = async (postId) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/psychedelia/posts/${postId}`);
      const post = await response.json();
      
      setTitle(post.title);
      setContent(post.content);
      setStatus(post.status);
      setEditingPostId(postId);
      
      if (post.image_url) {
        setImagePreview(`${process.env.REACT_APP_BACKEND_URL}${post.image_url}`);
      }
      
      toast.success('Post loaded for editing');
    } catch (error) {
      console.error('Error loading post:', error);
      toast.error('Failed to load post');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !content.trim()) {
      toast.error('Please fill in title and content');
      return;
    }

    setIsSubmitting(true);

    try {
      if (editingPostId) {
        // Update existing post
        const formData = new FormData();
        formData.append('title', title);
        formData.append('content', content);
        formData.append('status', status);
        if (image) {
          formData.append('image', image);
        }

        const response = await fetch(`${API}/psychedelia/posts/${editingPostId}`, {
          method: 'PUT',
          body: formData
        });

        if (response.ok) {
          toast.success('Post updated successfully! ✨');
          setTimeout(() => {
            window.location.href = '/living-room';
          }, 1000);
        } else {
          throw new Error('Failed to update post');
        }
      } else {
        // Create new post
        const formData = new FormData();
        formData.append('title', title);
        formData.append('content', content);
        formData.append('status', status);
        if (image) {
          formData.append('image', image);
        }

        const response = await fetch(`${API}/psychedelia/posts`, {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          toast.success('Post created successfully! ✨');
          // Reset form
          setTitle('');
          setContent('');
          setImage(null);
          setImagePreview(null);
          setStatus('published');
        } else {
          throw new Error('Failed to create post');
        }
      }
    } catch (error) {
      console.error('Error submitting post:', error);
      toast.error(editingPostId ? 'Failed to update post' : 'Failed to create post');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #F7F3EC 0%, #EFE7DD 50%, rgba(184, 211, 207, 0.2) 100%)',
      padding: '40px 20px'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '48px'
        }}>
          <IkiSymbol name="edit" size={48} />
          <h1 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '2.5rem',
            color: '#1E6B68',
            margin: '16px 0 8px'
          }}>
            {editingPostId ? 'Edit Living Room Post' : 'Create Living Room Post'}
          </h1>
          <p style={{
            fontSize: '1.05rem',
            color: '#666'
          }}>
            {editingPostId ? 'Update your daily insight' : 'Share today\'s insight with the community'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '20px',
          padding: '40px',
          boxShadow: '0 8px 24px rgba(30, 107, 104, 0.08)'
        }}>
          {/* Title */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#1E6B68',
              fontSize: '1rem'
            }}>
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., The Power of Presence"
              style={{
                width: '100%',
                padding: '14px',
                border: '2px solid rgba(184, 211, 207, 0.5)',
                borderRadius: '12px',
                fontSize: '1rem',
                fontFamily: "'Manrope', sans-serif"
              }}
            />
          </div>

          {/* Content */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#1E6B68',
              fontSize: '1rem'
            }}>
              Content
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Share your insight, reflection, or prompt..."
              rows={8}
              style={{
                width: '100%',
                padding: '14px',
                border: '2px solid rgba(184, 211, 207, 0.5)',
                borderRadius: '12px',
                fontSize: '1rem',
                fontFamily: "'Manrope', sans-serif",
                resize: 'vertical'
              }}
            />
          </div>

          {/* Image Upload */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#1E6B68',
              fontSize: '1rem'
            }}>
              Image (Optional)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px dashed rgba(184, 211, 207, 0.5)',
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            />
            {imagePreview && (
              <div style={{
                marginTop: '16px',
                borderRadius: '12px',
                overflow: 'hidden',
                maxWidth: '400px'
              }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block'
                  }}
                />
              </div>
            )}
          </div>

          {/* Status */}
          <div style={{ marginBottom: '32px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#1E6B68',
              fontSize: '1rem'
            }}>
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{
                width: '100%',
                padding: '14px',
                border: '2px solid rgba(184, 211, 207, 0.5)',
                borderRadius: '12px',
                fontSize: '1rem',
                fontFamily: "'Manrope', sans-serif",
                cursor: 'pointer'
              }}
            >
              <option value="published">Publish Now</option>
              <option value="draft">Save as Draft</option>
            </select>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: '100%',
              padding: '16px',
              background: isSubmitting 
                ? '#ccc' 
                : 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '24px',
              fontSize: '1.1rem',
              fontWeight: '600',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              transition: 'transform 0.2s ease',
              boxShadow: '0 4px 12px rgba(30, 107, 104, 0.3)'
            }}
            onMouseOver={(e) => !isSubmitting && (e.currentTarget.style.transform = 'translateY(-2px)')}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            {isSubmitting 
              ? (editingPostId ? 'Updating...' : 'Creating...') 
              : (editingPostId ? 'Update Post' : 'Create Post')
            }
          </button>
        </form>

        {/* Instructions */}
        <div style={{
          marginTop: '32px',
          padding: '24px',
          background: 'rgba(217, 203, 163, 0.2)',
          borderRadius: '16px',
          fontSize: '0.95rem',
          color: '#666',
          lineHeight: '1.7'
        }}>
          <h4 style={{ color: '#1E6B68', marginBottom: '12px' }}>Tips for Daily Posts:</h4>
          <ul style={{ paddingLeft: '20px', margin: 0 }}>
            <li>Keep it concise and meaningful</li>
            <li>Focus on one insight or question</li>
            <li>Use warm, inviting language</li>
            <li>Images should be calming and relevant</li>
            <li>Posts appear immediately in the Living Room</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default PsychedeliaAdmin;
